import { useState } from 'react';
import { Worker } from '../portals/admin/types';

export const useWorkers = () => {
  const [allWorkers, setAllWorkers] = useState<Worker[]>([]);
  const [availableWorkers, setAvailableWorkers] = useState<Array<{_id: string, name: string, email: string, region: string}>>([]);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [isEditingWorker, setIsEditingWorker] = useState(false);
  const [showWorkerSelection, setShowWorkerSelection] = useState(false);
  const [orderToAssign, setOrderToAssign] = useState<string | null>(null);

  const loadAllWorkers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/workers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllWorkers(data.workers || []);
      } else {
        console.error('Failed to fetch all workers, status:', response.status);
      }
    } catch (error) {
      console.error('Failed to load all workers:', error);
    }
  };

  const loadAvailableWorkers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/workers/available/list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableWorkers(data.workers || []);
      } else {
        console.error('Failed to fetch workers, status:', response.status);
      }
    } catch (error) {
      console.error('Failed to load available workers:', error);
    }
  };

  const assignWorkerToOrder = async (workerId: string, onSuccess: () => void) => {
    if (!orderToAssign) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/orders/bulk-assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          orderIds: [orderToAssign],
          workerId: workerId
        })
      });

      if (response.ok) {
        setShowWorkerSelection(false);
        setOrderToAssign(null);
        onSuccess();
        alert('Worker assigned successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to assign worker');
      }
    } catch (error) {
      console.error('Failed to assign worker:', error);
      alert('Failed to assign worker');
    }
  };

  const saveWorker = async (workerData: any) => {
    try {
      const token = localStorage.getItem('adminToken');
      const url = isEditingWorker
        ? `/api/workers/${selectedWorker?._id}`
        : '/api/workers';

      const method = isEditingWorker ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(workerData)
      });

      if (response.ok) {
        setShowWorkerModal(false);
        setSelectedWorker(null);
        setIsEditingWorker(false);
        loadAllWorkers();
        loadAvailableWorkers();
        alert(isEditingWorker ? 'Worker updated successfully!' : 'Worker created successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || `Failed to ${isEditingWorker ? 'update' : 'create'} worker`);
      }
    } catch (error) {
      console.error(`Failed to ${isEditingWorker ? 'update' : 'create'} worker:`, error);
      alert(`Failed to ${isEditingWorker ? 'update' : 'create'} worker`);
    }
  };

  const openWorkerSelectionModal = (orderId: string) => {
    setOrderToAssign(orderId);
    setShowWorkerSelection(true);
  };

  return {
    allWorkers,
    availableWorkers,
    selectedWorker,
    showWorkerModal,
    isEditingWorker,
    showWorkerSelection,
    orderToAssign,
    setSelectedWorker,
    setShowWorkerModal,
    setIsEditingWorker,
    setShowWorkerSelection,
    setOrderToAssign,
    loadAllWorkers,
    loadAvailableWorkers,
    assignWorkerToOrder,
    saveWorker,
    openWorkerSelectionModal
  };
};
